import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import Entypo from '@expo/vector-icons/Entypo';

import Colors from '../../constants/Colors';
import { AppText } from '../AppText';

const styles = StyleSheet.create({
  view: {
    width: 120,
    height: 40,
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.inputGrayBorder,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  button: {
    width: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

/**
 * Calculate a sensible default starting value within a given range.
 * - If the range includes 0, return 0
 * - Otherwise, return the value closest to 0 (lowest absolute value)
 */
function getDefaultValue(min: number, max: number): number {
  // If 0 is within the range, use it
  if (min <= 0 && max >= 0) {
    return 0;
  }

  // If range is all positive, return min (closest to 0)
  if (min > 0) {
    return min;
  }

  // If range is all negative, return max (closest to 0)
  return max;
}

/**
 * Normalize a value to be within the given range, or undefined if outside.
 * This ensures the component displays blank for out-of-range values.
 */
function normalizeValue(value: number | undefined, min: number, max: number): number | undefined {
  if (value === undefined) return undefined;
  if (value < min || value > max) return undefined;
  return value;
}

export type PlusMinusProps = {
  onChange: (value: number) => void;
  value?: number;
  min?: number;
  max?: number;
  testID?: string;
};

/**
 * A component for inputting a single number by incrementing with +1/-1 buttons.
 * This is a 'controlled' component - it does not maintain state internally,
 * but renders based on the provided `value` prop and calls the `onChange` when the
 * value is changed
 */
function PlusMinus(props: PlusMinusProps) {
  const { onChange, testID, value } = props;
  // if constraints are not provided then allow any valid integer
  const max = props.max ?? Number.MAX_SAFE_INTEGER;
  const min = props.min ?? Number.MIN_SAFE_INTEGER;

  // Normalize the value - treat out-of-range values as undefined
  const normalizedValue = normalizeValue(value, min, max);

  // Calculate sensible default starting value
  const defaultValue = getDefaultValue(min, max);

  const minusOne = (): void => {
    if (normalizedValue === undefined) {
      // When starting from undefined use sensible default
      onChange(defaultValue);
    } else {
      onChange(normalizedValue - 1);
    }
  };

  const plusOne = (): void => {
    if (normalizedValue === undefined) {
      // When starting from undefined use sensible default
      onChange(defaultValue);
    } else {
      onChange(normalizedValue + 1);
    }
  };
  const atMaxValue = normalizedValue === max;
  const atMinValue = normalizedValue === min;

  const plusColor = atMaxValue ? Colors.lightGray : Colors.black;
  const minusColor = atMinValue ? Colors.lightGray : Colors.black;

  return (
    <View style={styles.view} testID={testID}>
      <TouchableOpacity
        style={styles.button}
        onPress={minusOne}
        testID={`${testID}-minus`}
        disabled={atMinValue}
      >
        <Entypo name="minus" size={24} color={minusColor} />
      </TouchableOpacity>
      <AppText testID={`${testID}-value`}>{normalizedValue ?? ''}</AppText>
      <TouchableOpacity
        style={styles.button}
        onPress={plusOne}
        testID={`${testID}-plus`}
        disabled={atMaxValue}
      >
        <Entypo name="plus" size={24} color={plusColor} />
      </TouchableOpacity>
    </View>
  );
}

export default PlusMinus;
